import React from "react";
import type { Attachment, Conversation } from "../../TicketDetail";
import { AttachmentList, type DownloadMap } from "../AttachmentUI";
import { InitialsAvatar } from "./InitialsAvatar";
import { JiraText } from "./JiraText";

type Props = {
  conversations?: Conversation[];
  /** Who opened the ticket, for the avatar of the creation entry. */
  reporterName?: string;
  downloadingMap: DownloadMap;
  onDownload: (att: Attachment) => void;
};

type FriendlyDate = Conversation["created_at"];

const toFriendly = (value: FriendlyDate): string => {
  if (!value) return "";
  if (typeof value === "string") return value;
  return value.friendly || value.iso8601 || "";
};

// The single Jira account every dashboard reply is posted with.
const SHARED_REPLY_ACCOUNT = /customer support/i;

// "First Last: " — what the backend prepends to a dashboard reply so Jira
// keeps who wrote it. One line, before the first colon, and never the
// "https:" of a link.
const AUTHOR_PREFIX = /^([^\n:]{2,80}):(?!\/\/)[ \t]*/;

/**
 * Who wrote an entry, and its text without the author's name in front.
 *
 * Replies posted from the dashboard reach Jira as "Customer Support", with
 * the real author prepended to the text by the backend; CTX Reflector's
 * legacy entries carry no author at all, only that prefix. In both cases the
 * name moves from the text to the author, so they read like an agent's reply
 * (CTX-1283). An entry with a real author is left as Jira sent it, so an
 * agent writing "Nota: …" keeps the word in the text.
 */
export function getConversationEntry(conv: Conversation): {
  author: string;
  body: string;
} {
  const name = (conv.sender ?? conv.name ?? "").trim();
  const body = conv.body_text?.trim() ?? "";
  if (name && !SHARED_REPLY_ACCOUNT.test(name)) return { author: name, body };

  const prefix = body.match(AUTHOR_PREFIX);
  if (!prefix) return { author: "", body };
  return {
    author: prefix[1].trim(),
    body: body.slice(prefix[0].length).trim(),
  };
}

/**
 * The ticket's conversation as an activity list (CTX-1121): one entry per
 * comment with the author, the time, the body and its attachments inline, so
 * each attachment is shown once, next to the message it came with.
 */
export function TicketConversation({
  conversations = [],
  reporterName,
  downloadingMap,
  onDownload,
}: Props) {
  if (!conversations.length) {
    return (
      <p className="text-sm text-semantic-text-secondary">
        No replies yet. Write the first one below.
      </p>
    );
  }

  return (
    <ol className="flex flex-col divide-y divide-semantic-border-primary">
      {conversations.map((conv, idx) => {
        const { author, body } = getConversationEntry(conv);
        const when =
          toFriendly(conv.created_at_friendly) || toFriendly(conv.created_at);
        const attachments = Array.isArray(conv.attachments)
          ? conv.attachments
          : [];
        const isFromTeam = conv.incoming === false;
        // An entry with neither author nor text is the ticket's creation:
        // Jira files the attachments sent with the request there.
        const isCreation = !author && !body;
        const label = isCreation ? "Ticket created" : author;

        return (
          <li key={idx} className="flex gap-3 py-3 first:pt-0 last:pb-0">
            <InitialsAvatar
              name={isCreation ? reporterName : author || body}
              tone={isFromTeam ? "brand" : "neutral"}
            />
            <div className="min-w-0 flex-1">
              <p className="flex flex-wrap items-baseline gap-x-2 text-sm">
                {label && (
                  <span className="font-semibold text-semantic-text-primary">
                    {label}
                  </span>
                )}
                {when && (
                  <span className="text-xs text-semantic-text-secondary">
                    {when}
                  </span>
                )}
              </p>
              {body && (
                <p className="mt-1 whitespace-pre-line text-sm text-semantic-text-primary [overflow-wrap:anywhere]">
                  <JiraText text={body} />
                </p>
              )}
              {attachments.length > 0 && (
                <div className="mt-2">
                  <AttachmentList
                    attachments={attachments}
                    downloadingMap={downloadingMap}
                    onDownload={onDownload}
                      columns={2}
                  />
                </div>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
